/**
 * beaut-546 follow-up. Tells the roster paint on the reassign grid apart from
 * everything else drawn on it.
 *
 * The screen reloads by pushing a fresh set of events in through FullCalendar's
 * `addEventSource`, which only appends. On a date change that goes unnoticed —
 * the previous day's events fall outside the visible range — but re-reading the
 * same date leaves the old paint underneath the new one, which is how the red
 * "no roster" band outlived the shift the operator had just entered.
 *
 * So the paint has to be taken off before it is laid down again, and the only
 * thing separating it from the bookings is `extendedProps.type`: bookings carry
 * employee and status there and no `type` at all.
 */

/**
 * Every `type` emitted for an employee by `mapEmployeeSchedulesToEvents`, plus
 * `day-off`, which `change-schedule.component.ts` draws itself for a column the
 * roster call answered nothing for.
 *
 * Room types (`outside-shift`, `room-unavailable`) are deliberately absent:
 * the reassign grid has employee columns only, and a refresh there has no
 * business clearing a room's paint off any calendar that does show one.
 */
const SCHEDULE_BACKGROUND_TYPES = new Set([
  'no-schedule',
  'day-off',
  'off-hours',
  'break',
]);

/** True for one event of the roster paint, false for a booking. */
export const isScheduleBackgroundEvent = (event: any): boolean =>
  SCHEDULE_BACKGROUND_TYPES.has(event?.extendedProps?.type);

/**
 * The roster paint among `events` — what a refresh must remove first.
 *
 * Takes anything with `extendedProps`, so it reads both the plain objects the
 * mappers produce and the `EventApi` objects `calendarApi.getEvents()` returns.
 */
export const scheduleBackgroundEvents = <T>(events: readonly T[]): T[] =>
  events.filter((event) => isScheduleBackgroundEvent(event));
